import PropTypes from 'prop-types';
import React from 'react';

import { INVENTORY_LABELS } from 'components/PdpStoreInventory/constants';
import Tooltip from 'components/Tooltip';
import { largeUp, small } from 'constants/mediaQueries';
import Product from 'models/Product';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

import QuestionIcon from './QuestionIcon';

const Overview = ({
  product,
  backorderMessage,
  isFinalSale,
  soldOutMessage,
  productUpdatesTooltipEnabled,
  trackProductUpdatesTooltipView,
  showUrgencyMessaging,
}) => {
  const selectVariantKey = product.isGiftCard ? 'selectAmount' : 'selectSize';
  const showBackorderMessage = backorderMessage && !isFinalSale;

  return (
    <div className="Overview">
      <div className="Overview__title">{I18n.localize(`pdp.${selectVariantKey}`)}:</div>
      {isFinalSale && (
        <Tooltip content={I18n.localize('product.finalSaleExplanation')}>
          <div className="Overview__message Overview__message--warning">
            {I18n.localize('product.finalSaleMessage')} <QuestionIcon />
          </div>
        </Tooltip>
      )}
      {showBackorderMessage && (
        <Tooltip content={I18n.localize('pdp.backorderExplanation')}>
          <div className="Overview__message Overview__message--warning">
            {backorderMessage} <QuestionIcon />
          </div>
        </Tooltip>
      )}
      {!productUpdatesTooltipEnabled && soldOutMessage && (
        <Tooltip
          className="Overview__product-updates-tooltip"
          content={I18n.localize('pdp.colorwayUpdateForm.tooltip')}
          onView={trackProductUpdatesTooltipView}
        >
          <div className="Overview__message Overview__message--info">
            {I18n.localize('pdp.colorwayUpdateForm.colorSoldOut')}
          </div>
        </Tooltip>
      )}
      {productUpdatesTooltipEnabled && (
        <Tooltip
          className="Overview__product-updates-tooltip"
          content={I18n.localize('pdp.productUpdatesTooltip.content')}
          onView={trackProductUpdatesTooltipView}
        >
          <div className="Overview__message Overview__message--info">
            {I18n.localize('pdp.productUpdatesTooltip.anchor')}
          </div>
        </Tooltip>
      )}
      {showUrgencyMessaging && (
        <div className="Overview__message Overview__urgency-message">
          {INVENTORY_LABELS.low_inventory}
        </div>
      )}
      <style jsx>
        {`
          .Overview {
            display: flex;
            margin-bottom: ${Theme.get('spacing') * 2}px;
          }

          .Overview__title {
            font-size: ${Theme.get('font-size-small-px')};
            text-transform: uppercase;
            font-weight: ${Theme.get('font-weight-semi-bold')};
            letter-spacing: ${Theme.get('letter-spacing-large-px')};
            white-space: nowrap;
          }

          .Overview__message {
            color: ${Theme.get('colors-error')};
            font-size: ${Theme.get('font-size-small-px')};
            font-weight: ${Theme.get('font-weight-semi-bold')};
            letter-spacing: ${Theme.get('letter-spacing-tight')};
            margin-left: ${Theme.get('spacing-px')};
            margin-top: ${(Theme.get('spacing') * -1) / 3}px;
            text-align: left;
          }

          .Overview__message.Overview__urgency-message {
            margin-top: 0;
          }

          .Overview__message--info {
            color: ${Theme.get('colors-black')};
            text-decoration: underline;
            text-align: right;
          }

          .Overview__message--warning {
            color: color: ${Theme.get('colors-error')};
            display: flex;
          }

          @media ${small} {
            .Overview {
              display: flex;
              min-height: 14px;
            }
          }
  
          @media ${largeUp} {
            .Overview {
              display: flex;
              min-height: 14px;
            }
          }
        `}
      </style>
      <style jsx global>
        {`
          .Overview .QuestionIcon {
            margin-left: ${Theme.get('spacing') / 2}px;
            margin-top: ${Theme.get('spacing') / 3}px;
            flex-shrink: 0;
          }

          .Overview .Overview__product-updates-tooltip {
            margin-left: auto;
          }
        `}
      </style>
    </div>
  );
};

Overview.propTypes = {
  product: PropTypes.instanceOf(Product).isRequired,
  isFinalSale: PropTypes.bool,
  backorderMessage: PropTypes.string,
  soldOutMessage: PropTypes.string,
  productUpdatesTooltipEnabled: PropTypes.bool,
  trackProductUpdatesTooltipView: PropTypes.func.isRequired,
  showUrgencyMessaging: PropTypes.bool.isRequired,
};

Overview.defaultProps = {
  isFinalSale: false,
  backorderMessage: null,
  soldOutMessage: null,
  productUpdatesTooltipEnabled: false,
};

export default Overview;
